import { FC, ReactNode } from 'react';
import { Tooltip } from 'primereact/tooltip';
import { SimpleExpression, SimpleBinaryExpression, Operator, LogicalOperator } from '../../models/expression';
import { FieldDisplay } from './FieldDisplay';
import { LogicalOperatorChip } from './LogicalOperatorChip';
import { Bracket } from './Bracket';

interface ExpressionPreviewProps {
  expression: SimpleExpression | null;
  className?: string;
  style?: React.CSSProperties;
  showTooltip?: boolean;
}

const getOperatorSymbol = (op: Operator): string => {
  const symbols: Record<string, string> = {
    'Equal': '=',
    'NotEqual': '≠',
    'GreaterThan': '>',
    'LessThan': '<',
    'GreaterThanOrEqual': '≥',
    'LessThanOrEqual': '≤',
    'IsOneOf': 'IN',
    'Contains': 'CONTAINS',
    'ContainsAny': 'CONTAINS ANY',
    'ContainsAll': 'CONTAINS ALL',
    'StartsWith': 'STARTS WITH',
    'EndsWith': 'ENDS WITH',
    'IsEmpty': 'IS EMPTY',
  };
  return symbols[op] || op.toUpperCase();
};

const stripQuotes = (value: string): string => {
  return value.replace(/^['"]/, '').replace(/['"]$/, '');
};

const renderBinary = (binary: SimpleBinaryExpression, index: number): ReactNode => {
  const op = getOperatorSymbol(binary.operator);
  
  const isMultiValue = binary.operator === 'IsOneOf' || binary.operator === 'ContainsAny' || binary.operator === 'ContainsAll';
  const allValues = binary.right?.map(stripQuotes) || [];
  
  let displayRight: ReactNode;
  const tooltipId = `multi-value-${index}`;
  
  if (isMultiValue) {
    if (allValues.length > 1) {
      // Show only first item with ellipses
      const firstValue = allValues[0];
      const fullList = allValues.join(', ');
      
      displayRight = (
        <>
          <Tooltip target={`.${tooltipId}`} />
          <Bracket type="left" variant="list" />
          <span
            className={tooltipId}
            data-pr-tooltip={fullList}
            data-pr-position="top"
            style={{ cursor: 'default' }}
          >
            {firstValue}
            <span style={{ fontSize: '0.7rem', color: '#3b82f6' }}>, ...</span>
          </span>
          <Bracket type="right" variant="list" />
        </>
      );
    } else {
      displayRight = (
        <>
          <Bracket type="left" variant="list" />
          {allValues.join(', ') || '?'}
          <Bracket type="right" variant="list" />
        </>
      );
    }
  } else {
    displayRight = allValues.join(', ') || '?';
  }
  
  return (
    <span style={{ color: '#000' }}>
      {binary.isNot && <span style={{ fontWeight: 700, marginRight: '0.25rem' }}>NOT</span>}
      <Bracket type="left" />
      <FieldDisplay fieldValue={binary.left} />{' '}
      <span style={{ fontWeight: 700 }}>{op}</span>{' '}
      {displayRight}
      <Bracket type="right" />
    </span>
  );
};

const formatBinary = (binary: SimpleBinaryExpression): string => {
  const left = binary.left || '?';
  const op = getOperatorSymbol(binary.operator);
  
  let right: string;
  if (binary.right && binary.right.length > 3) {
    const firstThree = binary.right.slice(0, 3);
    right = firstThree.join(', ') + ', ...';
  } else {
    right = binary.right?.join(', ') || '?';
  }
  
  const base =
    binary.operator === 'IsOneOf' || binary.operator === 'ContainsAny' || binary.operator === 'ContainsAll'
      ? `${left} ${op} [${right}]`
      : `${left} ${op} ${right}`;
  return binary.isNot ? `NOT (${base})` : base;
};

const renderGroup = (expr: SimpleExpression, level: number = 0, baseIndex: number = 0, isFirstInParent: boolean = true, parentOperator?: LogicalOperator): ReactNode => {
  if (!expr.expressions || expr.expressions.length === 0) return 'TRUE';
  
  let currentIndex = baseIndex;
  
  const parts = expr.expressions.map((item, index) => {
    const isFirstItem = index === 0;
    // First item uses parent's indentation, rest use current level's indentation
    const itemLevel = (isFirstItem && isFirstInParent) ? Math.max(0, level - 1) : level;
    const indentSize = itemLevel * 1.5;
    
    if (item.expression) {
      // Render a simple binary expression
      const content = renderBinary(item.expression, currentIndex);
      currentIndex++;
      
      if (isFirstItem && isFirstInParent && parentOperator) {
        // First item of nested group - render inline with parent's operator
        return (
          <span key={index}>
            {content}
          </span>
        );
      } else if (isFirstItem) {
        return (
          <div 
            key={index} 
            style={{ 
              display: 'flex', 
              alignItems: 'flex-start', 
              gap: '0.5rem',
              paddingLeft: `${indentSize}rem`,
            }}
          >
            {content}
          </div>
        );
      } else {
        return (
          <div 
            key={index} 
            style={{ 
              display: 'flex', 
              alignItems: 'flex-start', 
              gap: '0.5rem', 
              marginTop: '0.25rem',
              paddingLeft: `${indentSize}rem`,
            }}
          >
            <LogicalOperatorChip operator={expr.logicalOperator} />
            <span>{content}</span>
          </div>
        );
      }
    } else if (item.expressionGroup) {
      const nestedGroup = item.expressionGroup;
      currentIndex += (nestedGroup.expressions?.length || 0);
      
      if (!isFirstItem) {
        return (
          <div 
            key={index}
            style={{ 
              display: 'flex', 
              alignItems: 'flex-start', 
              gap: '0.5rem', 
              marginTop: '0.25rem',
              paddingLeft: `${indentSize}rem`,
            }}
          >
            <LogicalOperatorChip operator={expr.logicalOperator} />
            <div style={{ flex: 1 }}>
              {renderGroup(nestedGroup, level + 1, baseIndex, true, expr.logicalOperator)}
            </div>
          </div>
        );
      } else {
        return (
          <div key={index}>
            {renderGroup(nestedGroup, level + 1, baseIndex, isFirstItem)}
          </div>
        );
      }
    }
    return null;
  }).filter(Boolean);
  
  if (expr.isNot) {
    return (
      <span>
        <span style={{ fontWeight: 700, marginRight: '0.25rem' }}>NOT</span>
        (<span style={{ display: 'inline-block', verticalAlign: 'top' }}>{parts}</span>)
      </span>
    );
  }
  
  return <>{parts}</>;
};

const formatGroup = (expr: SimpleExpression, level: number = 0): string => {
  if (!expr.expressions || expr.expressions.length === 0) return 'TRUE';
  
  const logicalOp = expr.logicalOperator === LogicalOperator.And ? 'AND' : 'OR';
  
  const parts = expr.expressions.map((item, index) => {
    let formatted = '';
    
    if (item.expression) {
      formatted = formatBinary(item.expression);
    } else if (item.expressionGroup) {
      formatted = `(${formatGroup(item.expressionGroup, level + 1)})`;
    }
    
    if (index === 0) {
      return formatted;
    } else {
      return `${logicalOp} ${formatted}`;
    }
  }).filter(Boolean);
  
  const joined = parts.join('\n');
  
  return expr.isNot ? `NOT (${joined})` : joined;
};

export const formatExpression = (expression: SimpleExpression | null): string => {
  if (!expression) return 'TRUE';
  
  try {
    return formatGroup(expression);
  } catch (error) {
    console.error('Error formatting expression:', error);
    return 'Invalid expression';
  }
};

export const ExpressionPreview: FC<ExpressionPreviewProps> = ({ 
  expression, 
  className = '', 
  style = {},
  showTooltip = true,
}) => {
  if (!expression) {
    return (
      <div
        className={`text-sm font-mono ${className}`}
        style={{ color: '#000', ...style }}
      >
        TRUE
      </div>
    );
  }
  
  const defaultStyle: React.CSSProperties = {
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    maxWidth: '100%',
    color: '#000',
    lineHeight: '1.5',
    ...style,
  };
  
  let content: ReactNode;
  try {
    content = renderGroup(expression);
  } catch (error) {
    console.error('Error rendering expression:', error);
    content = 'Invalid expression';
  }
  
  return (
    <div
      className={`text-sm font-mono ${className}`}
      style={defaultStyle}
    >
      {content}
    </div>
  );
};

